/**
 * Shared chrome for a single dev debug pill in the map bottom-right stack.
 */

import { forwardRef, useMemo } from "react";
import { getUIColorPalette } from "../../theme/ColorPalette";

export interface MapDevDebugPillProps {

    /** Short label shown on the pill. */
    "label": string;

    /** Click handler. */
    "onClick": () => void;

    /** Native `title` tooltip. */
    "title": string;

    /** Accessible name for screen readers. */
    "ariaLabel": string;

    /** Enables dark palette variants when true. */
    "isDarkUi": boolean;
}

/**
 * Renders one dev debug trigger pill (no absolute positioning — parent stack handles layout).
 *
 * @param props Pill label, handlers, and theme flag.
 * @param ref Optional ref forwarded to the underlying button (e.g. MiniModal anchor).
 * @returns Styled pill button.
 */
const MapDevDebugPill = forwardRef<HTMLButtonElement, MapDevDebugPillProps>( function MapDevDebugPill(
    { label, onClick, title, ariaLabel, isDarkUi },
    ref,
) {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    return (
        <button
            aria-label={ariaLabel}
            className="rounded-full border px-2 py-1 text-[10px] opacity-45 transition-opacity hover:opacity-80 active:opacity-90"
            onClick={onClick}
            ref={ref}
            style={{
                "backgroundColor": palette.overlayCardBackgroundStrong,
                "color":           palette.overlayTextSecondary,
                "borderColor":     palette.overlayBorder,
            }}
            title={title}
            type="button"
        >
            {label}
        </button>
    );
} );

export default MapDevDebugPill;
